import { Flex } from '@chakra-ui/react';
import Head from 'next/head';
import Layout from '@/components/shared/Layout';
import HourlyMain from '@/components/pages/buildings/HourlyMain';
import Empty from '@/components/pages/buildings/Empty';
import NearbyPlaces from '@/components/pages/buildings/NearbyPlaces';
import WithOneTimeToken from '@/components/hoc/withOneTimeToken';
import Footer from '@/components/shared/Footer';
import QRCode from 'qrcode';
import api from '@/services/apis';
// 直接導入 JavaScript 模組
import { places_result } from '@/assets/data/places_result';

function Building({ data, nearbyPlaces }) {
  // 增強 SEO 標題和描述
  let nearbyInfo = '';
  if (nearbyPlaces && nearbyPlaces.length > 0) {
    nearbyInfo = `附近有 ${nearbyPlaces.length} 個餐廳、商店和咖啡店。`;
  }

  const title = `${data?.name} | ${data?.city}${data?.zone}停車位，${nearbyInfo}`;
  const description = `${data?.city}${data?.zone}${
    data?.road
  } 停車位臨停共 ${Math.max(data?.space_total, data?.space_number)} 格，${
    data?.name
  }臨停停車位，${nearbyInfo}`;

  return (
    <Layout>
      <Head>
        <title key='title'>{title}</title>
        <meta name='description' key='description' content={description} />
        <meta key='og_title' property='og:title' content={title} />
        <meta
          key='og_description'
          property='og:description'
          content={description}
        />
        <meta
          key='og_image'
          property='og:image'
          content={data?.spaces[0]?.image1 || data?.spaces[0]?.image2}
        />
        {/* 結構化數據，提高 SEO */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'ParkingFacility',
              name: data?.name,
              address: {
                '@type': 'PostalAddress',
                addressLocality: data?.city,
                addressRegion: data?.zone,
                streetAddress: data?.road,
              },
              description: description,
              hasMap: data?.share_link,
              geo: {
                '@type': 'GeoCoordinates',
                latitude: data?.coordinate?.lat,
                longitude: data?.coordinate?.lon,
              },
            }),
          }}
        />
      </Head>

      <Flex direction='column' minH='100vh'>
        {data ? (
          <>
            <HourlyMain data={data} />

            {/* 使用獨立的 NearbyPlaces 組件 */}
            {nearbyPlaces && nearbyPlaces.length > 0 && (
              <NearbyPlaces
                buildingName={data.name}
                buildingLat={data?.coordinate?.lat}
                buildingLng={data?.coordinate?.lon}
                nearbyPlaces={nearbyPlaces}
              />
            )}
            <Footer />
          </>
        ) : (
          <Empty />
        )}
      </Flex>
    </Layout>
  );
}

export async function getServerSideProps(context) {
  const { id } = context.query;

  // 獲取建築詳情
  const response = await api.fetchBuildingDetail({ id });
  if (response.error) {
    return {
      props: {
        data: null,
        error: response.error,
        nearbyPlaces: null,
      },
    };
  }

  // 生成二維碼
  const qrCodeBase64 = await QRCode.toDataURL(response.content.share_link);

  // 從導入的 JS 模組中獲取附近地點資料
  let nearbyPlaces = [];
  try {
    // 查找對應 ID 的建築物數據
    const buildingData = places_result.find(
      (building) => building.building_id === id,
    );

    if (buildingData && buildingData.nearby_places) {
      nearbyPlaces = buildingData.nearby_places;
    }
  } catch (error) {
    console.error('處理附近地點資料失敗:', error);
    // 失敗時使用空陣列，不影響主要功能
    nearbyPlaces = [];
  }

  return {
    props: {
      data: {
        id,
        ...response.content,
        share_link_qrcode_image: qrCodeBase64,
      },
      nearbyPlaces,
    },
  };
}

export default WithOneTimeToken(Building);
